import ProfileService from "@/shared/services/ProfileService";
import {
  AvatarUploadResponse,
  ProcessOtpRequest,
  ProfileResponse,
} from "@/shared/types/profile";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";

// Хук для получения профиля пользователя
export const useProfile = () => {
  return useQuery({
    queryKey: ["profile"],
    queryFn: ProfileService.getProfile,
    staleTime: 5 * 60 * 1000, // 5 минут
    gcTime: 10 * 60 * 1000, // 10 минут
  });
};

// Хуки для статистики и настроек уведомлений удалены,
// так как соответствующие методы удалены из ProfileService

// Хук для обновления имени пользователя
export const useUpdateUsername = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ProfileService.updateUsername,
    onSuccess: (data: ProfileResponse) => {
      // Обновляем кэш профиля
      queryClient.setQueryData(["profile"], data);

      // Инвалидируем связанные запросы
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (error: AxiosError) => {
      console.error("Username update failed:", error);
    },
  });
};

// Хук для обновления email
export const useUpdateEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ProfileService.updateEmail,
    onSuccess: (data: ProfileResponse) => {
      // Обновляем кэш профиля
      queryClient.setQueryData(["profile"], data);

      // Инвалидируем связанные запросы
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (error: AxiosError) => {
      console.error("Email update failed:", error);
    },
  });
};

// Хук для отправки подтверждения email удален,
// так как метод sendEmailVerification удален из ProfileService

// Хук для изменения пароля
export const useChangePassword = () => {
  return useMutation({
    mutationFn: ProfileService.changePassword,
    onError: (error: AxiosError) => {
      console.error("Password change failed:", error);
    },
  });
};

// Хук для загрузки аватарки
export const useUploadAvatar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ProfileService.uploadAvatar,
    onSuccess: (data: AvatarUploadResponse) => {
      // Обновляем профиль с новой аватаркой
      queryClient.setQueryData(
        ["profile"],
        (oldData: ProfileResponse | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            user: {
              ...oldData.user,
              avatar: data.avatarUrl,
              updatedAt: new Date().toISOString(),
            },
          };
        }
      );

      // Инвалидируем связанные запросы
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (error: AxiosError) => {
      console.error("Avatar upload failed:", error);
    },
  });
};

// Хук для удаления аватарки
export const useDeleteAvatar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ProfileService.deleteAvatar,
    onSuccess: (data: ProfileResponse) => {
      // Обновляем кэш профиля
      queryClient.setQueryData(["profile"], data);

      // Инвалидируем связанные запросы
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (error: AxiosError) => {
      console.error("Avatar deletion failed:", error);
    },
  });
};

// Хук для обработки OTP при активации аккаунта
export const useProcessOtp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProcessOtpRequest) => ProfileService.processOtp(data),
    onSuccess: () => {
      // Инвалидируем профиль после успешной обработки OTP
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (error: AxiosError) => {
      console.error("OTP processing failed:", error);
    },
  });
};

// Хук для обработки OTP при смене email
export const useProcessEmailOtp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProcessOtpRequest) =>
      ProfileService.processEmailOtp(data),
    onSuccess: () => {
      // Инвалидируем профиль после успешной обработки OTP
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (error: AxiosError) => {
      console.error("Email OTP processing failed:", error);
    },
  });
};
